<template>
  <div v-if="editingData" class="plv-ia-qn-edit">
    <div class="plv-ia-qn-edit__main">
      <div class="plv-ia-qn-edit__body">
        <div ref="form" class="plv-ia-qn-edit-form">
          <div class="plv-ia-qn-edit-name plv-ia-clearfix pws-vclass-qn-edit-name">
            <label>{{ t('plvIA.questionnaire.name') }}</label>
            <div>
              <div class="plv-ia-qn-edit-name__wrap">
                <textarea
                  ref="title"
                  v-model.trim="title"
                  :placeholder="t('plvIA.questionnaire.enterName')"
                  :maxlength="maxLen.title"
                ></textarea>
                <span><span :class="{ 'plv-ia-err': strLen(title || '') > maxLen.title }">{{ strLen(title || '') }}</span>/{{ maxLen.title }}</span>
              </div>
            </div>
          </div>

          <div class="plv-flex-row plv-ia-qn-edit-content plv-mt-20">
            <label class="plv-mr-8">{{ t('plvIA.common.privacy') }}:</label>
            <PrivacySwitch
              class="plv-ia-qn-edit-content__privacy"
              :enable.sync="privacyData.required"
              :content.sync="privacyData.content"
              :tips="t('plvIA.questionnaire.privacyTips')"
            />
          </div>

          <div class="plv-ia-qn-edit-content">
            <EditQuestion
              v-for="(item, index) in questions"
              :ref="`question_${item.questionId}`"
              :key="item.questionId"
              :question-data="item"
              :question-index="index"
              :question-options="item.options"
              :is-last="index + 1 >= questions.length"
              @edit="handleQuestionOp"
            />
          </div>
        </div>
      </div>

      <div class="plv-ia-qn-edit__footer plv-ia-clearfix">
        <div class="plv-ia-fl">
          <button class="plv-ia-btn-new plv-ia-btn-blue pws-vclass-btn-blue" @click="handleAdd">{{ t('plvIA.questionnaire.addQuestion') }}</button>
        </div>
        <div class="plv-ia-fr">
          <button class="plv-ia-btn-new plv-ia-btn-blue pws-vclass-btn-blue" @click="handleSave">{{ t('plvIA.common.save') }}</button>
          <button class="plv-ia-btn-new pws-vclass-btn-new" @click="handleCancel">{{ t('plvIA.common.cancel') }}</button>
        </div>
      </div>
    </div>

    <!-- 删除后将无法恢复，确认删除？ -->
    <PopupConfirm
      ref="delete-modal"
      :tips="t('plvIA.questionnaire.confirmDelTips')"
      :ensure-txt="t('plvIA.common.delete')"
      :cancel-txt="t('plvIA.common.cancel')"
      :ensure-btn-cls="'plv-ia-btn-new plv-ia-btn-orange-2 pws-vclass-btn-orange-2'"
      @ensure="ensureDelQuestion"
      @cancel="cancelDelQuestion"
    />
    <!-- 放弃编辑后问卷将无法恢复，确认放弃？ -->
    <PopupConfirm
      ref="cancel-editing-modal"
      :tips="t('plvIA.questionnaire.confirmDiscardTips')"
      :ensure-txt="t('plvIA.questionnaire.discard')"
      :cancel-txt="t('plvIA.questionnaire.continueEditing')"
      @ensure="ensureCancelEditing"
      @cancel="cancelCancelEditing"
    />
  </div>
</template>

<script>
import Locale from '@/mixins/locale';
import { strLen } from '@polyv/utils/src/string';
import { debounce } from 'lodash-es';
import PopupConfirm from '@/components/popup/Confirm';
import PrivacySwitch from '@/components/privacy-switch/privacy-switch.vue';
import EditQuestion from './EditQuestion';
import { maxLen, maxQuestionsCount, getQuestionModel, getPrivacyModel } from '@/assets/app/questionnaire/helper';
import { cloneJSON } from '@polyv/utils/src/lang';

export default {
  name: 'QuestionnaireEdit',
  components: { EditQuestion, PopupConfirm, PrivacySwitch },
  mixins: [Locale],

  props: {
    editingData: {
      type: Object,
      default: null
    },
    // TODO 研讨会暂时隐藏隐私协议功能
    useSeminarChatApi: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      maxLen,
      deleteQuestionIndex: -1, // 要被删除的题目

      title: this.editingData.name, // 问卷标题
      questions: this.editingData.questions || [],

      privacyData: this.editingData.privacy || getPrivacyModel() // 隐私协议数据
    };
  },

  methods: {
    strLen,

    // 删除题目弹窗
    toggleDeleteModal(show) {
      show ? this.$refs['delete-modal'].show() : this.$refs['delete-modal'].hide();
    },

    // 确认删除题目
    ensureDelQuestion() {
      this.toggleDeleteModal(false);
      this.questions.splice(this.deleteQuestionIndex, 1);
      this.deleteQuestionIndex = -1; // 重置
    },

    // 取消删除题目
    cancelDelQuestion() {
      this.toggleDeleteModal(false);
    },

    // 放弃编辑弹窗
    toggleCancelEditingModal(show) {
      show ? this.$refs['cancel-editing-modal'].show() : this.$refs['cancel-editing-modal'].hide();
    },

    // 确认放弃编辑
    ensureCancelEditing() {
      this.toggleCancelEditingModal(false);
      this.$emit('cancel');
    },

    // 继续编辑
    cancelCancelEditing() {
      this.toggleCancelEditingModal(false);
    },

    handleCancel() {
      this.toggleCancelEditingModal(true);
    },

    handleSave: debounce(function() {
      const data = {
        name: this.title,
        questionnaireId: this.editingData.questionnaireId,
        questions: this.questions.map((question) => {
          const ref = this.$refs[`question_${question.questionId}`];
          const { question: questionData } = ref[0];
          return {
            ...questionData,
            score: questionData.score !== '' ? Number(questionData.score) : '',
            answer: questionData.type === 'C' ? questionData.answer.join('') : questionData.answer,
          };
        }),
      };

      // 检验问卷内容的完整性
      const errData = this.checkQnTitle(data) || this.checkQuestions(data) || this.checkPrivacySwitch(this.privacyData);
      if (errData) {
        this.handleErrData(errData);
        return;
      }

      // 设置了隐私协议才放到questions里
      this.setPrivacyQuestion(data);

      this.$emit('save', data);
    }, 500),

    handleErrData(data) {
      if (!data.tips) return;

      this.$iaMessage.default(data.tips);

      const { questionnaireTitle, question: questionId, name, score, option: optionIndex } = data;

      if (questionnaireTitle) { // 问卷标题
        this.$refs.title.focus();
        return;
      }

      if (!questionId) return;
      const refs = this.$refs[`question_${questionId}`];
      if (!refs) return;
      const $question = refs[0];
      if (!$question) return;

      if (name) { // 题目内容
        $question.$refs.name.focus();
      } else if (score) { // 题目分数
        $question.$refs.score.focus();
      } else if (typeof option === 'number') { // 题目选项内容
        $question.$refs.option[optionIndex].focus();
      }
    },

    // 添加题目
    handleAdd() {
      const len = this.questions.length;
      if (len >= maxQuestionsCount) {
        this.$iaMessage.default(`${this.t('plvIA.questionnaire.notExceed')} ${maxQuestionsCount} ${this.t('plvIA.questionnaire.questionCount')}`);
        return;
      }
      const questionModel = getQuestionModel();
      this.$set(this.questions, this.questions.length, questionModel);
      this.$nextTick(() => {
        const wrap = this.$refs.form;
        wrap.scrollTop = wrap.scrollHeight;
      });
    },

    // 操作题目（上移/下移/删除）
    handleQuestionOp({ type, index }) {
      if (type === 'delete') {
        this.deleteQuestionIndex = index;
        this.toggleDeleteModal(true);
      } else {
        const nextIndex = type === 'up' ? index - 1 : index + 1; // 移动到哪个位置
        const question = this.questions.splice(index, 1)[0];
        this.questions.splice(nextIndex, 0, question);
      }
    },

    checkQnTitle(questionnaire) {
      const validate = function(val) {
        if (!val) return this.t('plvIA.questionnaire.titleNotEmptyTips');
        if (strLen(val) > maxLen.title) return `${this.t('plvIA.questionnaire.titleMaxLenTips')} ${maxLen.title / 2} ${this.t('plvIA.questionnaire.characters')}`;
        return '';
      }.bind(this);

      const tips = validate(questionnaire.name);
      return !tips ?
        null :
        {
          questionnaireTitle: true,
          tips,
        };
    },

    checkQuestionTitle(val) {
      if (!val) return this.t('plvIA.questionnaire.contentNotEmptyTips');
      if (strLen(val) > maxLen.questionTitle) return `${this.t('plvIA.questionnaire.contentMaxLenTips')} ${maxLen.questionTitle / 2} ${this.t('plvIA.questionnaire.characters')}`;
      return null;
    },

    checkQuestionScore(questionObj) {
      if (!questionObj.isScorable) return '';
      if (typeof questionObj.score !== 'number' || isNaN(questionObj.score)) return this.t('plvIA.questionnaire.scoreNotEmptyTips');
      return '';
    },

    checkAnswer(questionObj) {
      if (questionObj.isScorable && !questionObj.answer) return this.t('plvIA.questionnaire.enterAnswer');
      return '';
    },

    checkOption(val) {
      if (!val) return this.t('plvIA.questionnaire.optionContentNotEmptyTips');
      if (strLen(val) > maxLen.option) return `${this.t('plvIA.questionnaire.optionContentMaxLenTips')} ${maxLen.option / 2} ${this.t('plvIA.questionnaire.characters')}`;
      return '';
    },

    checkOptionList(questionId, optionList) {
      for (let optionIndex = 0; optionIndex < optionList.length; optionIndex++) {
        const option = optionList[optionIndex];
        const tips = this.checkOption(option);
        if (!tips) continue;
        return {
          tips,
          question: questionId,
          option: optionIndex,
        };
      }
      return null;
    },

    checkQuestions(questionnaire) {
      const list = questionnaire.questions;

      if (!list || list.length <= 0) {
        return {
          tips: this.t('plvIA.questionnaire.questionsNotEmptyTips')
        };
      }

      for (let i = 0; i < list.length; i++) {
        const question = cloneJSON(list[i]);

        // 1. 检查题目标题
        let tips = this.checkQuestionTitle(question.name);
        if (tips) {
          return {
            question: question.questionId,
            name: true,
            tips
          };
        }

        // 2. 检查分数填写
        tips = this.checkQuestionScore(question);
        if (tips) {
          return {
            question: question.questionId,
            score: true,
            tips
          };
        }

        // 3. 检查选项内容
        if (question.type !== 'Q') {
          const tipsData = this.checkOptionList(question.questionId, question.options);
          if (tipsData) return tipsData;

          // 4. 检查答案的完整性
          tips = this.checkAnswer(question);
          if (tips) {
            return {
              tips
            };
          }
        }
      }
      return null;
    },

    checkPrivacySwitch(privacyData) {
      const notNull = '隐私协议内容不能空';
      const lengthLimit = '隐私协议内容超出长度限制';
      let tips = '';

      if (privacyData && privacyData.required === 'Y') {
        if (!privacyData.content) {
          tips = notNull;
        }

        // 限制富文本内容长度
        if (privacyData.content && privacyData.content.length > 200) {
          tips = lengthLimit;
        }
      }

      if (tips) {
        return {
          tips
        };
      }
    },

    // 设置隐私协议的数据到 待提交的数据中
    setPrivacyQuestion(data) {
      if (this.privacyData && data && this.privacyData.required === 'Y') {
        data.privacyContent = this.privacyData.content;
        data.privacyEnabled = this.privacyData.required;
      }
    }
  }
};
</script>

<style lang="scss">
@import '../../assets/styles/mixins.scss';
$imgPrefix: './images/';

@keyframes rotation {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.plv-ia-qn-edit {
  user-select: none;
  width: 100%;
  height: 100%;
  color: #333;
  font-size: 14px;
}

.plv-ia-qn-edit__main {
  width: 100%;
  height: 100%;
  position: relative;
}

.plv-ia-qn-edit__body {
  height: 100%;
  box-sizing: border-box;
  padding-bottom: 80px;
  position: relative;
}

.plv-ia-qn-edit-form {
  padding-top: 24px;
  padding-left: 24px;
  width: 100%;
  height: 100%;
  position: relative;
  overflow: auto;
  box-sizing: border-box;
}

.plv-ia-qn-edit-name {
  >* {
    float: left;
    line-height: 40px;
  }

  >label {
    margin-right: 8px;
    width: $questionaire-label-left;
    text-align: right;
  }

  >div {
    @include input();
    width: 400px;
    padding: 0 2px;
  }
}

.plv-ia-qn-edit-name__wrap {
  padding-bottom: 22px;
  height: 100%;
  box-sizing: border-box;

  >span {
    line-height: 22px;
    right: 8px;
    position: absolute;
    bottom: 0;
    font-size: 12px;
    color: #9e9e9e;
    user-select: none;
  }
}

.plv-ia-qn-edit-content {
  align-items: center;

  & > label {
    width: $questionaire-label-left;
    text-align: right;
  }

  & > .plv-ia-qn-edit-content__privacy {
    flex: 1;
  }
}

.plv-ia-qn-edit__footer {
  width: 100%;
  position: absolute;
  bottom: 0;
  left: 0;
  padding: 24px;
  box-sizing: border-box;
  padding-bottom: 24px;

  .plv-ia-fr {
    button {
      width: 64px;
    }

    button + button {
      margin-left: 8px;
    }
  }
}

</style>
